// P4 — **NOTIFICATIONS IN THE MEMBER MENU, AND THE MEASUREMENT THAT PRODUCED THIS FILE IS THE ITEM.**
//
// P4-3 reads *"the menu entry is THE door and the My Room bell RETIRES."* Measured before a line was
// written (R25): **THERE IS NO BELL.** `src/pushOptIn.js` says in its own comment that *"the bell in
// My Room stays as the manual way in (her ruling: the bell remains as the fallback)"*, and
// `server/push-routes.js` repeats it — and nothing in `src/` has ever rendered a manual push control
// of any kind. The header bell is `HouseInbox`, the notifications INBOX, which subscribes to nothing.
//
// **SO THE FALLBACK THIS HOUSE TOLD ITSELF IT HAD DID NOT EXIST, AND WHAT THAT COSTS IS THE ROUND:**
// `shouldOfferPush` refuses to offer on ANY prior answer, `later` included and deliberately so, and
// the sheet is the only place a member has ever been asked. **A member who tapped "Not now" was never
// asked again, ever, and had nowhere to go.** No error, no log, nothing on her screen — this house's
// most expensive shape, and it was written down as handled in two files (R125's dead receipt, in
// prose rather than in a path).
//
// **AND `/api/push/unsubscribe` HAS HAD ZERO CLIENT CALL SITES SINCE THE DAY IT SHIPPED** (R38's
// trap, on the one route whose entire job is to let her stop). Turning notifications OFF was not a
// thing this site could do.
//
// This module imports one roster and nothing else, so BOTH sides read it and
// `scripts/p4-push-settings.mjs` RUNS it — the entry requirement every law in `src/` is held to.
import { PUSH_ANSWERS } from "./pushOptIn.js";

// ---------------------------------------------------------------------------
// P4-1 — THE STATE. Her three, plus the three the platform adds, plus R18's.
//
// **`unknown` IS FIRST AND IT IS NOT DECORATION.** "This device does not receive" and "we have not
// looked at this device yet" are different facts, and a panel that draws OFF while it is still
// reading tells a subscribed member she is unsubscribed and invites her to fix something that is not
// broken. It is W5-2/R39's third state and R64's four-way, on a control rather than on a list.
// ---------------------------------------------------------------------------
export const PUSH_PANEL_STATES = {
  UNKNOWN: "unknown",
  NEEDS_INSTALL: "needs-install",
  UNSUPPORTED: "unsupported",
  BLOCKED: "blocked",
  ON: "on",
  OFF: "off",
};
export const PUSH_PANEL_STATE_VALUES = Object.values(PUSH_PANEL_STATES);

// **THE ORDER IS THE LAW AND IT IS HELD AS AN ORDER** (R52/R138/R155: a clause that runs after the
// one it corrects is not a clause). Two of the six rungs are rulings rather than arithmetic:
//
//   · **`needs-install` IS ASKED BEFORE `unsupported`, AND ONLY WHERE PUSH IS GENUINELY UNSUPPORTED.**
//     On iOS, web push exists only in the app installed to the home screen (16.4+), so an iPhone in
//     Safari reports exactly what a browser that will never do this reports. **Telling an iPhone
//     member her browser cannot do notifications is a true-looking sentence with NO REPAIR IN IT**,
//     while "add it to your home screen" is true and is one gesture. Conditioning it on `!supported`
//     is what keeps it honest and self-correcting: the day iOS supports push in the browser, that
//     branch stops being reached without anybody editing this file.
//
//   · **PERMISSION GRANTED WITH NO LIVE SUBSCRIPTION IS `off`, NOT `on`.** The account's grant and
//     the browser's registration are two different facts (P3-2's whole finding), and the one this
//     panel reports is whether THIS DEVICE RECEIVES. She taps once and it mints with no prompt,
//     because the permission is already hers.
export function pushPanelState(device) {
  if (!device || typeof device !== "object") return PUSH_PANEL_STATES.UNKNOWN;
  const {
    supported = false, installed = false, iosDevice = false,
    permission = "default", subscribed = false,
  } = device;
  if (!supported) {
    return iosDevice && !installed ? PUSH_PANEL_STATES.NEEDS_INSTALL : PUSH_PANEL_STATES.UNSUPPORTED;
  }
  if (permission === "denied") return PUSH_PANEL_STATES.BLOCKED;
  return subscribed ? PUSH_PANEL_STATES.ON : PUSH_PANEL_STATES.OFF;
}

// ---------------------------------------------------------------------------
// P4-2 — WHAT A TAP DOES, AND WHERE THERE IS NOTHING TO TAP.
//
// **P4-4's OWN RULING, AS ARITHMETIC:** *"an unsupported context says so honestly rather than
// offering a moment that cannot complete."* So four of the six states carry NO control at all —
// a blocked member cannot be un-blocked from in here (no site can re-summon the ask after a denial),
// an uninstalled iPhone has nothing to subscribe with, and a device still being read has not
// answered yet. A button that cannot do its own job teaches her to distrust the ones that can.
// ---------------------------------------------------------------------------
export const PUSH_ACTIONS = { SUBSCRIBE: "subscribe", UNSUBSCRIBE: "unsubscribe" };

export function pushPanelAction(state) {
  if (state === PUSH_PANEL_STATES.OFF) return PUSH_ACTIONS.SUBSCRIBE;
  if (state === PUSH_PANEL_STATES.ON) return PUSH_ACTIONS.UNSUBSCRIBE;
  return null;
}

// ---------------------------------------------------------------------------
// **THE CROSSING THAT MAKES THIS A LAW RATHER THAN A COMPONENT, AND IT IS A LIVE DEFECT THIS ITEM
// WOULD HAVE SHIPPED IF IT HAD BEEN WRITTEN THE OBVIOUS WAY.**
//
// `healDecision` runs on EVERY launch of the installed app and answers `RESUBSCRIBE` for
// `answered = "granted"` with no live subscription and the OS permission still granted. **That is
// exactly the state an opt-out leaves behind.** So an opt-out that swept the row and left the
// account saying `granted` would be UNDONE BY THE HEAL on her very next launch — silently, with no
// prompt (the re-mint is deliberately prompt-free), and nothing on her screen to say her own choice
// had been reversed. The panel would look correct in every screenshot ever taken of it.
//
// **SO THE OPT-OUT RECORDS AN ANSWER, AND `off` IS A FOURTH VALUE RATHER THAN A REUSED `later`.**
// Reusing `later` would work today — every receiving side either derives from the roster
// (`pushAnswerValid`, `shouldOfferPush`) or reports the raw string (the morning strip, the beacon
// sweep) and NOTHING anywhere branches on a specific value, measured. What it would cost is R18's
// distinction on the one column that records what a member chose: **"she dismissed an ask" and "she
// went into her settings and turned this off" are different facts**, and her own diagnostic surfaces
// print that column verbatim, so collapsing them makes a deliberate refusal read as a shrug.
//
// The roster below is what a fixture drives `healDecision` over. `granted` is written by the SERVER
// (`handleSubscribe`, so the grant and the row land together and cannot disagree); `off` is written
// by the panel, because the browser is where the unsubscribe happens and only the client knows it
// landed.
// ---------------------------------------------------------------------------
export const PANEL_ANSWERS = [PUSH_ANSWERS.GRANTED, PUSH_ANSWERS.OFF];

// ---------------------------------------------------------------------------
// S7-1 — **A SECOND LINE IS DRAWN ONLY WHERE SHE CANNOT ACT FROM HERE, AND IT IS DERIVED FROM THE
// ACTION RATHER THAN ROSTERED BESIDE IT** (R31: the crossing stops existing rather than being
// watched).
//
// Her ruling: *"the explainer sentence DIES — the panel becomes the TITLE, the HONEST STATE and the
// ONE BUTTON matching state. The BLOCKED teaching stays only where blocked, one line. Nothing
// else."* Read against the six states, that is one sentence rather than an exception list:
//
//   · ON and OFF have a BUTTON. The button is the answer, and a paragraph above it explaining what
//     notifications are is a brochure standing between her and the control.
//   · `unknown` has no button and nothing to teach — the title *Checking this device* IS the state,
//     and it is gone in a moment.
//   · `blocked`, `needs-install` and `unsupported` have no button **because the repair is somewhere
//     else** — device settings, the home screen, another browser. Those three keep ONE line, and
//     what it carries is the WHERE rather than an explanation of the feature.
//
// So: **a teaching exists exactly where there is no action and the state is not the transient
// read.** The roster below is the WHICH-ROW; this function is the WHETHER, and the fixture holds the
// two against each other in both directions, so a state added tomorrow cannot quietly grow prose or
// quietly lose its repair.
// ---------------------------------------------------------------------------
export function pushPanelTeaches(state) {
  return pushPanelAction(state) === null && state !== PUSH_PANEL_STATES.UNKNOWN;
}

// ---------------------------------------------------------------------------
// THE WORDS, AS DATA. One row per state, so a state added to the roster tomorrow either carries its
// own copy or the gate names it — R28/R29's biconditional rather than a `switch` whose default is
// silence (R17's, and R32's fallthrough that no value check could see).
//
// `note: null` is a DECLARED absence rather than a missing key (R18): it says this state has been
// looked at and has nothing she must do elsewhere, which is a different fact from a row somebody
// forgot to write. The panel renders a note where there IS one, so an unrostered state — which falls
// back to `unknown`'s row — draws none, which is the right answer for a state nobody declared.
// ---------------------------------------------------------------------------
export const PUSH_PANEL_KEYS = {
  [PUSH_PANEL_STATES.UNKNOWN]: { title: "push.checking", note: null },
  [PUSH_PANEL_STATES.NEEDS_INSTALL]: { title: "push.needsInstall", note: "push.needsInstallNote" },
  [PUSH_PANEL_STATES.UNSUPPORTED]: { title: "push.unsupported", note: "push.unsupportedNote" },
  [PUSH_PANEL_STATES.BLOCKED]: { title: "push.blocked", note: "push.blockedNote" },
  [PUSH_PANEL_STATES.ON]: { title: "push.on", note: null },
  [PUSH_PANEL_STATES.OFF]: { title: "push.off", note: null },
};

export const PUSH_ACTION_KEYS = {
  [PUSH_ACTIONS.SUBSCRIBE]: "push.turnOn",
  [PUSH_ACTIONS.UNSUBSCRIBE]: "push.turnOff",
};

// A state nobody rostered would render a blank heading over a blank sentence, which is W12-5's
// DATE-OF-BIRTH-over-nothing one control down. It answers the KEYS of a state that is definitionally
// present instead, and the gate holds the roster TOTAL so this is never reached in production.
export function pushPanelCopy(state) {
  return PUSH_PANEL_KEYS[state] || PUSH_PANEL_KEYS[PUSH_PANEL_STATES.UNKNOWN];
}
